import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type { BuildFileHit } from '@uniticket/shared';
import { cn } from '@/lib/utils';
import type { BuildFilesState } from '@/hooks/useAddressValidation';
import { BuildFileCard } from '@/components/dmp/DmpConsolePanel';

// Les pastilles « Service file » et « Deployment list » de la console DMP, posées dans la fenêtre de validation à
// côté de Capacité et SSV / TSA (demande du user, 22-10 : pour tous les validateurs, pas seulement la console).
//
// Une pastille par fichier CHARGÉ : un fichier jamais chargé n'a pas de réponse à donner, une pastille grise se
// lirait « absent ». Même chose si la lecture échoue : rien plutôt qu'un faux « absent ».
//
// Sur une adresse à plusieurs logements, la pastille compte (« Service file 3/5 », choix du user du 22-10) :
// VERTE si tous les logements sont dans le fichier, AMBRE si une partie seulement, GRISE si aucun. La règle de
// SSV / TSA (vert dès qu'un seul) ne convient pas ici : le logement absent est justement ce qu'on cherche.

export const PILL_KINDS = ['SERVICE', 'DEPLOYMENT'] as const;
export type PillKind = (typeof PILL_KINDS)[number];

/** Un logement de la pastille : son JVID et ce qui le nomme dans la fiche (le n° de boîte, à défaut le JVID). */
export interface BuildFileUnit {
  jvid: number;
  label: string;
}

/** Ce que la fiche ouverte montre : un logement et sa réponse pour CE fichier. */
export interface BuildFileUnitHit extends BuildFileUnit {
  hit: BuildFileHit;
}

export function BuildFilePills({
  state, units, onOpen,
}: { state: BuildFilesState; units: BuildFileUnit[]; onOpen: (kind: PillKind, hits: BuildFileUnitHit[]) => void }) {
  const { t } = useTranslation(['tickets']);
  if (units.length === 0) return null;

  if (state.status === 'loading') {
    return (
      <>
        {PILL_KINDS.map((kind) => (
          <span
            key={kind}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500"
          >
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
            {t(`tickets:dmpLookup.buildFile.${kind}`)}
          </span>
        ))}
      </>
    );
  }
  if (state.status !== 'ready' || !state.byJvid) return null;
  const byJvid = state.byJvid;

  return (
    <>
      {PILL_KINDS.map((kind) => {
        const hits: BuildFileUnitHit[] = [];
        for (const u of units) {
          const hit = (byJvid[String(u.jvid)] ?? []).find((h) => h.kind === kind);
          if (hit) hits.push({ ...u, hit });
        }
        // Fichier jamais chargé : aucune entrée, donc pas de pastille.
        if (hits.length === 0) return null;
        const found = hits.filter((h) => h.hit.found).length;
        const all = found === hits.length;
        const none = found === 0;
        const label = t(`tickets:dmpLookup.buildFile.${kind}`);
        const made = hits[0].hit.fileDate ?? hits[0].hit.uploadedAt;
        const hint = t('tickets:validation.buildFilePill.hint', {
          meaning: t(`tickets:dmpLookup.buildFile.${kind}Hint`),
          date: new Date(made).toLocaleDateString('fr-BE'),
        });
        return (
          <button
            key={kind}
            type="button"
            // L'en-tête de groupe n'est pas cliquable, mais ses voisines le sont : même verrou que `CapacityPill`.
            onClick={(e) => { e.stopPropagation(); onOpen(kind, hits); }}
            title={hint}
            aria-label={`${label}${hits.length > 1 ? ` ${found}/${hits.length}` : ''} — ${t('tickets:dmpLookup.buildFile.openDetail')}`}
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold transition-colors',
              all
                ? 'border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                : none
                  ? 'border-slate-300 bg-slate-100 text-slate-500 hover:bg-slate-200'
                  : 'border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200',
            )}
          >
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', all ? 'bg-emerald-500' : none ? 'bg-slate-300' : 'bg-amber-500')}
              aria-hidden="true"
            />
            {label}
            {hits.length > 1 && <span className="font-semibold tabular-nums">{found}/{hits.length}</span>}
          </button>
        );
      })}
    </>
  );
}

/** La fiche derrière la pastille. Un seul logement : la fiche de la console telle quelle. Plusieurs : un logement par
 *  ligne, présent ou absent, la fiche de la console dépliée au clic. */
export function BuildFileUnitsDetail({ title, hits }: { title: string; hits: BuildFileUnitHit[] }) {
  const { t } = useTranslation(['tickets']);
  const [open, setOpen] = useState<number | null>(null);
  if (hits.length === 1) return <BuildFileCard hit={hits[0].hit} />;

  const found = hits.filter((h) => h.hit.found).length;
  return (
    <div className="space-y-2 p-4">
      <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      <p className="text-xs text-slate-600">
        {t('tickets:validation.buildFilePill.summary', { found, count: hits.length })}
      </p>
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
        {hits.map((h) => (
          <li key={h.jvid}>
            <button
              type="button"
              onClick={() => setOpen((v) => (v === h.jvid ? null : h.jvid))}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-slate-50"
            >
              {open === h.jvid
                ? <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
                : <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />}
              <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{h.label}</span>
              <span className="font-mono text-slate-400">{h.jvid}</span>
              <span
                className={cn(
                  'whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold',
                  h.hit.found ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500',
                )}
              >
                {h.hit.found ? t('tickets:dmpLookup.buildFile.present') : t('tickets:dmpLookup.buildFile.absent')}
              </span>
            </button>
            {open === h.jvid && (
              <div className="px-3 pb-3">
                <BuildFileCard hit={h.hit} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
